/**
 * Auth Context - Options Oracle V2
 * Provides authentication state and methods across the app
 */

import type { User } from 'firebase/auth'
import { signInWithRedirect } from 'firebase/auth'
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore'
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import { getFirebaseAuth, getFirestore, getGoogleProvider } from '../services/firebase'
import {
    completeGoogleSignIn,
    signOut as firebaseSignOut,
    onAuthChange,
    onUserDataChange,
    signInWithEmail,
    signInWithGoogle,
    signUpWithEmail,
    type UserData,
} from '../services/firebaseAuth'

interface AuthContextType {
    // Firebase User (raw)
    firebaseUser: User | null
    // Firestore User Data (extended)
    userData: UserData | null
    // Loading state
    loading: boolean
    // Auth methods
    signInWithGoogle: typeof signInWithGoogle
    signInWithGoogleRedirect: () => Promise<void> // Added missing type
    signInWithEmail: typeof signInWithEmail
    signUpWithEmail: typeof signUpWithEmail
    signOut: () => Promise<void>
    // Computed helpers
    isAuthenticated: boolean
    isAdmin: boolean
    isApproved: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
    const [firebaseUser, setFirebaseUser] = useState<User | null>(null)
    const [userData, setUserData] = useState<UserData | null>(null)

    // Optimistic Init: ALWAYS start true to match server, then flip on client if cache exists.
    const [loading, setLoading] = useState(true)

    // Optimistic Data Load Effect
    useEffect(() => {
        if (typeof window !== 'undefined') {
            try {
                const cached = localStorage.getItem('auth_user_data')
                const cachedUid = localStorage.getItem('auth_uid')
                if (cached && cachedUid) {
                    const data = JSON.parse(cached)
                    if (data.uid === cachedUid) {
                        setUserData(data)
                        setLoading(false)
                        return
                    }
                }
            } catch (e) {
                console.error('[Auth] Failed to load cached user data', e)
            }
            // If we are here, we didn't hit the cache fast-path
            // But we are mounted, so check if we *should* be loading or not.
            // Actually, if no cache, we might still be waiting for Firebase.
            // So we don't auto-set loading(false) here unless we want to assume "not logged in" initially?
            // Better to let the onAuthChange handle the "not logged in" state (user === null).
            // BUT, for hydration match, we must match server (loading=true).
            // If we don't have cache, we remain loading=true until Firebase responds.
        }
    }, [])

    useEffect(() => {
        if (typeof window !== 'undefined') {
            // If we have userData, we are definitely done loading
            if (userData) {
                setLoading(false)
            }
        }
    }, [userData])

    const [authCheckComplete, setAuthCheckComplete] = useState(false)

    // Handle redirect result on mount (for SSO redirects)
    useEffect(() => {
        if (typeof window === 'undefined') return

        completeGoogleSignIn().then((result) => {
            if (result?.success && result.userData) {
                console.log('[AuthContext] Redirect sign-in completed')
            }
        })
    }, [])

    // Listen to Firebase Auth state changes
    useEffect(() => {
        if (typeof window === 'undefined') {
            setLoading(false)
            setAuthCheckComplete(true)
            return
        }

        const unsubAuth = onAuthChange(async (user) => {
            setFirebaseUser(user)
            setAuthCheckComplete(true) // Firebase has spoken

            if (user) {
                localStorage.setItem('auth_uid', user.uid)
                try {
                    const token = await user.getIdToken(false)
                    const { setCachedIdToken } = await import('../services/firebase')
                    setCachedIdToken(token)
                } catch (e) {
                    console.error('[Auth] Failed to cache token', e)
                }
            } else {
                setUserData(null)
                setLoading(false)
                localStorage.removeItem('auth_user_data')
                localStorage.removeItem('auth_uid')
                const { setCachedIdToken } = await import('../services/firebase')
                setCachedIdToken(null)
            }
        })

        return () => unsubAuth()
    }, [])

    // Online Presence Heartbeat
    useEffect(() => {
        if (!firebaseUser || typeof window === 'undefined') return

        const pingPresence = () => {
            try {
                const db = getFirestore()
                updateDoc(doc(db, 'users', firebaseUser.uid), {
                    lastLogin: serverTimestamp(),
                }).catch((err: any) => console.error('[Auth] Heartbeat failed:', err))
            } catch (err) {
                console.error('[Auth] Heartbeat exception:', err)
            }
        }

        // Fire immediately upon app load/connection
        pingPresence()

        // Then continue firing every 5 minutes
        const interval = setInterval(pingPresence, 5 * 60 * 1000)

        return () => clearInterval(interval)
    }, [firebaseUser])

    // Listen to User Data changes (Firestore)
    useEffect(() => {
        if (!firebaseUser) return

        const unsubUserData = onUserDataChange(firebaseUser.uid, (data) => {
            if (data) {
                setUserData(data)
                // Cache for next load
                localStorage.setItem('auth_user_data', JSON.stringify(data))
            }
            // Whether we got data or null (if doc doesn't exist yet/deleted), stop loading
            setLoading(false)
        })

        return () => unsubUserData()
    }, [firebaseUser])

    // Sign out wrapper
    const signOut = async () => {
        await firebaseSignOut()
        setFirebaseUser(null)
        setUserData(null)
        localStorage.removeItem('auth_user_data')
        localStorage.removeItem('auth_uid')
    }

    // Computed values
    // Optimistic Auth: Authenticated if we have valid UserData AND (Firebase User is confirmed OR Firebase check hasn't finished yet)
    // This prevents redirecting to login while waiting for Firebase to Initialize if we have cached credentials.
    const isAuthenticated = !!userData && (!!firebaseUser || !authCheckComplete)
    const isAdmin = userData?.role === 'admin'
    const isApproved = userData?.status === 'approved'

    return (
        <AuthContext.Provider
            value={{
                firebaseUser,
                userData,
                loading,
                signInWithGoogle,
                signInWithGoogleRedirect: async () => {
                    const auth = getFirebaseAuth()
                    const provider = getGoogleProvider()
                    await signInWithRedirect(auth, provider)
                },
                signInWithEmail,
                signUpWithEmail,
                signOut,
                isAuthenticated,
                isAdmin,
                isApproved,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
